import { useEffect, useState } from "react";
import {
  EventNamesEnum,
  EventPropertiesEnum,
  INDIA_PHONE_NUMBER_LENGTH,
  MAX_MOBILE_NUMBER_LENGTH,
  MIN_MOBILE_NUMBER_LENGTH,
} from "@/constants/constants";
import { useClientTranslations } from "@/hooks/useClientTranslations";
import { useTrackingAnalytics } from "@/hooks/useTrackingAnalytics";
import { setFocusOnTv } from "@/utils/client.helpers";
import { closeApp, isTvEnv } from "@/utils/common.helpers";
import { tvBackButtonOverrideHandler } from "@/utils/tv.helpers";
import * as UserTypes from "@/types/user.types";
import { FocusKeyEnum } from "@/utils/focusKeys.helpers";

interface UseSendOTPOptions {
  initialMobileNumber?: string;
  sendOTPState?: UserTypes.SendOTPStateType;
  onMobileNumberComplete?: () => void;
  maxDigits?: number;
}

export const useSendOTP = (options: UseSendOTPOptions = {}) => {
  const { t } = useClientTranslations();
  const { trackEvent } = useTrackingAnalytics();
  const {
    initialMobileNumber = "",
    sendOTPState,
    onMobileNumberComplete,
    maxDigits = INDIA_PHONE_NUMBER_LENGTH,
  } = options;

  const [mobileNumber, setMobileNumber] = useState<string>(
    sendOTPState?.mobileNumber || initialMobileNumber,
  );
  const [errorMessage, setErrorMessage] = useState<string>("");

  // Sync mobile number when sendOTPState changes
  useEffect(() => {
    if (sendOTPState?.mobileNumber) {
      // Validate and truncate to maxDigits when syncing from state
      const validatedValue = sendOTPState.mobileNumber
        .replace(/\D/g, "") // Remove non-digits
        .slice(0, maxDigits); // Limit to maxDigits
      setMobileNumber(validatedValue);
    }
  }, [sendOTPState?.mobileNumber]);

  // Validation helpers
  const isValidMobileNumber = (value: string): boolean => {
    if (maxDigits === INDIA_PHONE_NUMBER_LENGTH) {
      return value.length === INDIA_PHONE_NUMBER_LENGTH;
    }
    return (
      value.length >= MIN_MOBILE_NUMBER_LENGTH &&
      value.length <= MAX_MOBILE_NUMBER_LENGTH
    );
  };

  const isNumeric = (value: string): boolean => {
    return /^\d*$/.test(value);
  };

  const trackLoginInitiation = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const redirectionAfterLoginTo = urlParams.get("redirectionAfterLoginTo");
    const sourceSplit = redirectionAfterLoginTo?.split("/") ?? [];
    const sourcePage =
      sourceSplit.length > 0 ? sourceSplit[sourceSplit.length - 1] : "";
    trackEvent({
      eventName: EventNamesEnum.LOGIN_INITIATED,
      properties: {
        login_method: EventPropertiesEnum.LOGIN_METHOD_PHONE,
        login_stage: EventPropertiesEnum.PHONE_TYPING,
        source_page_name: sourcePage,
      },
      sendTo: [],
    });
  };

  const validateAndSetMobileNumber = (value: string): void => {
    if (!isNumeric(value) || value.length > maxDigits) {
      return;
    }

    setMobileNumber(value);

    // Clear error if valid
    if (errorMessage && isValidMobileNumber(value)) {
      setErrorMessage("");
    }

    // Track login initiation on first character
    if (value.length === 1) {
      trackLoginInitiation();
    }

    // Focus button when complete (TV only)
    if (isValidMobileNumber(value) && isTvEnv()) {
      setFocusOnTv(FocusKeyEnum.SEND_OTP_BUTTON);
      onMobileNumberComplete?.();
    }
  };

  const showValidationError = (): void => {
    setErrorMessage(t("auth_screen.phone_number_error_msg"));
  };

  const handleInputFocus = () => {
    setErrorMessage("");
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (!isValidMobileNumber(mobileNumber)) {
      e.preventDefault();
      showValidationError();
    } else {
      setErrorMessage("");
    }
  };

  // TV back button handler
  useEffect(() => {
    tvBackButtonOverrideHandler.set(() => {
      closeApp();
    }, false);
    return () => {
      tvBackButtonOverrideHandler.clear();
    };
  }, []);

  return {
    mobileNumber,
    errorMessage,
    setErrorMessage,
    isValidMobileNumber,
    isNumeric,
    validateAndSetMobileNumber,
    showValidationError,
    handleInputFocus,
    handleSubmit,
  };
};
